import React from 'react';
import { Link } from 'react-router-dom';
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { useRef } from "react";
import { MdOutlineArrowUpward } from "react-icons/md";

const SectionList = ({ id, title, description, images, date, documentId }) => {
  // Séparer la date en mois jour et année pour l'affichage
  const [year, month, day] = date ? date.split('-') : ['', '', ''];
  // conversion du mois en string
  const months = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aout', 'Sept', 'Oct', 'Nov', 'Dec'];
  const monthString = months[parseInt(month) - 1];

  const imageUrl = images ? `${images.url}` : 'https://via.placeholder.com/300';

  return (
    <article className="flex flex-col sm:flex-row transition hover:shadow-xl p-4" key={id}>
      <div className="p-2 sm:rotate-180 sm:[writing-mode:_vertical-lr]">
        <time
          dateTime="2022-10-10"
          className="flex items-center justify-between gap-4 text-xs font-bold uppercase text-gray-900"
        >
          <span className="w-px flex-1 bg-gray-900/10 sm:w-auto sm:h-px md:w-px md:h-auto"></span>
        </time>
      </div>

      <div className="sm:basis-56 md:basis-64 lg:basis-72">
        <img
          alt={title}
          src={`${imageUrl}`}
          className="aspect-square h-full object-contain max-h-80"
        />
      </div>

      <div className="flex flex-1 flex-col justify-between mt-4 sm:mt-0 sm:ml-4">
        <div className="border-s border-gray-900/10 p-4 sm:border-l-transparent sm:p-6">
          <h3 className="font-bold uppercase text-gray-900 sm:text-lg md:text-xl lg:text-2xl">
            {title}
          </h3>

          <p className="mt-2 line-clamp-3 text-sm/relaxed text-gray-700 font-medium sm:text-base md:text-lg lg:text-lg">
            {description.length > 0 ? description : 'Pas de description disponible.🙃'}
          </p>
        </div>

        <div className="sm:flex sm:items-end sm:justify-end mt-4 sm:mt-0">
          <Link to={`${documentId}`} key={id}>
            <MagnetButton title={title} />
          </Link>
        </div>
      </div>
    </article>
  );
};

const MagnetButton = ({ title }) => {
  const ref = useRef(null);

  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const xSpring = useSpring(x, {
    mass: 3,
    stiffness: 400,
    damping: 50,
  });
  const ySpring = useSpring(y, {
    mass: 3,
    stiffness: 400,
    damping: 50,
  });

  const transform = useMotionTemplate`translateX(${xSpring}px) translateY(${ySpring}px)`;

  const handleMouseMove = (e) => {
    if (!ref.current) return;

    const { height, width, left, top } = ref.current.getBoundingClientRect();

    x.set(e.clientX - (left + width / 2));
    y.set(e.clientY - (top + height / 2));
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.button
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ transform }}
      transition={{ type: "spring", stiffness: 200, damping: 20 }}
      className="group relative grid h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 lg:h-28 lg:w-28 place-content-center rounded-full border-2 border-black transition-colors duration-700 ease-out"
    >
      <MdOutlineArrowUpward className="pointer-events-none relative z-10 rotate-45 text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-black transition-all duration-700 ease-out group-hover:rotate-90" />

      <div className="pointer-events-none absolute inset-0 z-0 scale-0 rounded-full bg-white transition-transform duration-700 ease-out group-hover:scale-100" />

      <motion.svg
        initial={{ rotate: 0 }}
        animate={{ rotate: 360 }}
        transition={{
          duration: 25,
          repeat: Infinity,
          repeatType: "loop",
          ease: "linear",
        }}
        style={{
          top: "50%",
          left: "50%",
          x: "-50%",
          y: "-50%",
        }}
        width="100"
        height="100"
        className="pointer-events-none absolute z-10"
      >
        <path
          id="circlePath"
          d="M50,50 m-50,0 a50,50 0 1,0 100,0 a50,50 0 1,0 -100,0"
          fill="none"
        />
        <text>
          <textPath
            href="#circlePath"
            fill="black"
            className="fill-black text-base sm:text-sm md:text-base font-light uppercase opacity-0 transition-opacity duration-700 ease-out group-hover:opacity-100"
          >
            {title ? title : 'Voir plus'}
          </textPath>
        </text>
      </motion.svg>
    </motion.button>
  );
};

export default SectionList;